import { Box, Button, CloseButton, Flex, Icon, Spinner, Text, useBreakpointValue } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiAlertCircle, FiAlertTriangle, FiMessageSquare } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { urls } from "../../urls";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useFloatsAboveBottomBar, usePageFrame } from "../layout/pageFrame";
import { MENU_WIDTH_PX, useSidebarMode } from "../layout/sidebarMode";
import { useAssistantContext } from "./AssistantProvider";
import { useAssistantPanel } from "./assistantPanelContext";
import { conversationPill, type ConversationPillStatus } from "./conversationPill";

const GAP_PX = 16;
const THUMB_TARGET_PX = 44;
const PILL_CHROME_PX = 4 + 4 + 2;
const ROOMY_BUTTON_PX = 32;

const ReservesRoomBelowContent = ({ heightPx }: { heightPx: number }) => {
  useFloatsAboveBottomBar({ heightPx });
  return null;
};

export const AssistantPill = () => {
  const { t } = useTranslation();
  const { panelOpen, openPanel, reopenPanel } = useAssistantPanel();
  const { turns, isRunning, pendingConfirmation, subject } = useAssistantContext();
  const { pathname } = useLocation();
  const personId = useLoggedInWithOrgContextState().me.id;
  const { mode } = useSidebarMode(personId);
  const { hasShell, bottomBarPx } = usePageFrame();
  const menuBesidePage = useBreakpointValue({ base: false, lg: true }) ?? false;
  const roomy = useBreakpointValue({ base: false, md: true }) ?? false;
  const [dismissedConversation, setDismissedConversation] = useState<string | null>(null);

  const pill = conversationPill({
    turns,
    isRunning,
    awaitsConfirmation: pendingConfirmation != null,
    stepHelp: {
      buttonLabel: t("Help me with this step"),
      stepName: subject?.step?.label,
      titleFor: (step) => t("Help me with the step «{{step}}»", { step }),
    },
  });
  const dismissed = pill != null && pill.conversationKey === dismissedConversation;
  if (!pill || panelOpen || dismissed || pathname === urls.app.assistant) return null;

  const left = GAP_PX + (hasShell && menuBesidePage ? MENU_WIDTH_PX[mode] : 0);
  const count = t("{{count}} messages", { count: pill.messageCount });
  const statusText = {
    idle: null,
    working: t("The assistant is working"),
    waitingForYou: t("Waiting for your answer"),
    failed: t("The answer failed – open to try again"),
  }[pill.status];
  const state = statusText ? `, ${statusText}` : "";

  return (
    <Flex
      role="region"
      aria-label={t("Closed conversation") ?? ""}
      position="fixed"
      bottom={`${GAP_PX + bottomBarPx}px`}
      left={`${left}px`}
      zIndex="sticky"
      align="center"
      gap="1"
      maxW={`calc(100vw - ${left + GAP_PX}px)`}
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      rounded="full"
      shadow="md"
      pl="1"
      pr="1"
      py="1"
    >
      <ReservesRoomBelowContent heightPx={(roomy ? ROOMY_BUTTON_PX : THUMB_TARGET_PX) + PILL_CHROME_PX} />
      <Button
        variant="ghost"
        size="sm"
        rounded="full"
        minW="0"
        minH={roomy ? undefined : `${THUMB_TARGET_PX}px`}
        px="2"
        onClick={reopenPanel ?? openPanel}
        aria-label={`${t("Open the conversation")}: ${pill.title}, ${count}${state}`}
      >
        <Box display="inline-flex" flexShrink={0}>
          <PillIcon status={pill.status} />
        </Box>
        <Flex
          aria-hidden
          direction={roomy ? "row" : "column"}
          align={roomy ? "baseline" : "flex-start"}
          gap={roomy ? "2" : "0"}
          lineHeight={roomy ? undefined : "short"}
          ml="2"
          minW="0"
          overflow="hidden"
        >
          <Text
            fontSize="sm"
            fontWeight="medium"
            color="smText"
            noOfLines={1}
            maxW={roomy ? "96" : "100%"}
          >
            {pill.title}
          </Text>
          <Text
            fontSize="xs"
            color={pill.status === "failed" ? "smError" : pill.status === "waitingForYou" ? "smWarning" : "smMuted"}
            whiteSpace="nowrap"
          >
            {pill.status === "working" ? t("Working …") : (statusText ?? count)}
          </Text>
        </Flex>
      </Button>
      <CloseButton
        size="sm"
        boxSize={roomy ? undefined : `${THUMB_TARGET_PX}px`}
        flexShrink={0}
        rounded="full"
        color="smMuted"
        aria-label={t("Hide the conversation pill") ?? ""}
        onClick={() => setDismissedConversation(pill.conversationKey)}
      />
    </Flex>
  );
};

const PillIcon = ({ status }: { status: ConversationPillStatus }) => {
  if (status === "working") return <Spinner size="sm" color="smPrimary" />;
  if (status === "failed") return <Icon as={FiAlertTriangle} boxSize="4" color="smError" />;
  if (status === "waitingForYou") return <Icon as={FiAlertCircle} boxSize="4" color="smWarning" />;
  return <Icon as={FiMessageSquare} boxSize="4" color="smPrimary" />;
};
